import { type FC, useEffect, useState } from "react";

import { Alert } from "../../common/Alert";
import { Text } from "../../common/Text";
import { MarkdownArticleLayout, SidebarItem } from "./MarkdownArticleLayout";

interface VersionInfo {
  version: string;
  date: string;
  latest?: boolean;
}

interface VersionsManifest {
  versions: VersionInfo[];
}

export const ReleaseNotesView: FC = () => {
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>("");
  const [manifestError, setManifestError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/docs/versions.json")
      .then((res) => res.json())
      .then((data: VersionsManifest) => {
        setVersions(data.versions);
        const version =
          data.versions.find((v) => v.latest)?.version ??
          data.versions[0]?.version;
        if (version) {
          setSelectedVersion(version);
        }
      })
      .catch(() => setManifestError("Failed to load versions"));
  }, []);

  const selectedInfo = versions.find((v) => v.version === selectedVersion);

  if (manifestError) {
    return <Alert variant="error">{manifestError}</Alert>;
  }

  return (
    <MarkdownArticleLayout
      sidebarTitle="Versions"
      sidebar={versions.map((v) => (
        <SidebarItem
          key={v.version}
          selected={v.version === selectedVersion}
          onClick={() => setSelectedVersion(v.version)}
          label={
            <>
              v{v.version}
              {v.latest && (
                <Text
                  variant="label"
                  className="ml-1.5 text-emerald-500 dark:text-emerald-300"
                >
                  Latest
                </Text>
              )}
            </>
          }
          detail={v.date}
        />
      ))}
      contentTitle={`v${selectedVersion} Release Notes`}
      contentSubtitle={selectedInfo?.date}
      loadingLabel="Loading release notes..."
      markdownUrl={selectedVersion ? `/docs/v${selectedVersion}.md` : null}
      errorPrefix={`Failed to load release notes for v${selectedVersion}`}
    />
  );
};
